import { zodResolver } from "@hookform/resolvers/zod";
import { EnvelopeSimpleIcon, PhoneCallIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/nextjs";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AiOutlineLoading } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";
import { HiArrowRight, HiCheckCircle, HiExclamationCircle, HiX } from "react-icons/hi";
import { PhoneInput } from "react-international-phone";
import z from "zod";
import { cn } from "../../lib/utils";
import { locations } from "../ContactSection";
import {
	BackgroundImage,
	ContactIcon,
	ContactIcons,
	ContentWrapper,
	Description,
	FeedbackCard,
	FeedbackClose,
	FeedbackIcon,
	FeedbackOverlay,
	FeedbackText,
	FeedbackTitle,
	Flag,
	FormCard,
	Location,
	LocationPhone,
	LocationsGrid,
	LocationsText,
	LocationsWrapper,
	LocationTitle,
	Section,
	StyledInput,
	StyledPhoneInputWrapper,
	StyledTextarea,
	SubmitButton,
	Title,
	TitleWrapper,
} from "./styles";

// Lista de provedores de e-mail gratuitos
const FREE_EMAIL_PROVIDERS = [
	"gmail.com",
	"hotmail.com",
	"outlook.com",
	"yahoo.com",
	"yahoo.com.br",
	"live.com",
	"icloud.com",
	"me.com",
	"aol.com",
	"protonmail.com",
	"mail.com",
	"gmx.com",
	"yandex.com",
	"zoho.com",
	"inbox.com",
	"mail.ru",
	"terra.com.br",
	"bol.com.br",
	"uol.com.br",
	"ig.com.br",
	"r7.com",
	"oi.com.br",
];

const schema = z.object({
	name: z.string().min(1, "nameRequired"),
	email: z
		.string()
		.min(1, "emailRequired")
		.email("emailInvalid")
		.refine((email) => {
			const domain = email.split("@")[1]?.toLowerCase();
			return !FREE_EMAIL_PROVIDERS.includes(domain);
		}, "emailMustBeCorporate"),
	phone: z
		.string()
		.min(1, "phoneRequired")
		.refine((phone) => {
			// Remove espaços e caracteres especiais para validação
			const cleanPhone = phone.replace(/[\s\-()]/g, "");
			// Deve ter pelo menos +XX e mais 8 dígitos (formato mínimo internacional)
			return cleanPhone.length >= 11 && cleanPhone.startsWith("+");
		}, "phoneMinLength"),
	company: z.string().min(1, "companyRequired"),
	subject: z.string().min(1, "subjectRequired"),
});

/* A API respondeu, mas recusou o lead: validação do lambda, chave inválida,
   throttling do API Gateway. Vira exceção para cair no mesmo tratamento das
   falhas de rede, com status e corpo preservados para o Sentry. */
class ContactRequestError extends Error {
	constructor(status, responseBody) {
		super(`POST /contact-us respondeu ${status}`);
		this.name = "ContactRequestError";
		this.status = status;
		this.responseBody = responseBody;
	}
}

export default function ContactSectionForm({ title, description, isMobile, buttonText }) {
	const { t, i18n } = useTranslation();
	const [phone, setPhone] = useState("");
	const [phoneTouched, setPhoneTouched] = useState(false);
	// "success" | "error" | null — retorno do envio, exibido no modal central
	const [feedback, setFeedback] = useState(null);

	const form = useForm({
		defaultValues: {
			name: "",
			email: "",
			phone: "",
			company: "",
			subject: "",
		},
		mode: "onTouched",
		resolver: zodResolver(schema),
	});

	const {
		register,
		reset,
		handleSubmit: hookFormHandleSubmit,
		formState,
		setValue,
		trigger,
	} = form;

	useEffect(() => {
		const phoneValue = phone || "";
		setValue("phone", phoneValue);
		if (phoneTouched) {
			trigger("phone");
		}
	}, [phone, setValue, trigger, phoneTouched]);

	// Esc fecha o modal, junto com o clique fora e o botão de fechar
	useEffect(() => {
		if (!feedback) return undefined;
		const onKeyDown = (event) => {
			if (event.key === "Escape") setFeedback(null);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [feedback]);

	const handleSubmit = async (data) => {
		const apiUrl = `${process.env.NEXT_PUBLIC_VERZEL_FUNCTIONS_HOST}/contact-us`;

		try {
			const urlParams = new URLSearchParams(window.location.search);
			const isABM = urlParams.get("ABM") === "true";
			const origin = isABM ? "SITE ABM" : "SITE";

			const headers = new Headers();
			headers.append("Content-Type", "application/json");
			headers.append(
				"X-Api-Key",
				process.env.NEXT_PUBLIC_VERZEL_LAMBDA_FUNCTIONS_KEY,
			);

			const response = await fetch(apiUrl, {
				method: "POST",
				headers,
				body: JSON.stringify({
					...data,
					phone: phone,
					origin: origin,
				}),
			});

			if (!response.ok) {
				const responseBody = await response.text().catch(() => "<corpo ilegível>");
				throw new ContactRequestError(response.status, responseBody);
			}

			setFeedback("success");
			form.reset();
			setPhone("");
			setPhoneTouched(false);
		} catch (error) {
			console.error(error);
			Sentry.captureException(error, {
				tags: {
					form: 'contact-section-form',
					// separa "a API recusou" de "não conseguimos falar com a API"
					failure: error instanceof ContactRequestError ? `http-${error.status}` : 'network',
				},
				extra: {
					apiUrl,
					status: error.status,
					responseBody: error.responseBody,
					formData: { name: data.name, email: data.email, phone: phone }
				}
			});
			setFeedback("error");
		}
	};

	return (
		<Section id="contact-us">
			{/* Background image with gradient */}
			<BackgroundImage>
				<img
					src="/landings/assets/images/products/contact-bg.jpg"
					alt=""
					aria-hidden="true"
				/>
			</BackgroundImage>

			<ContentWrapper>
				{/* Title + Description */}
				<TitleWrapper>
					<Title
						id="contact-form"
						dangerouslySetInnerHTML={{ __html: title }}
					/>
					{description && (
						<Description
							dangerouslySetInnerHTML={{ __html: description }}
						/>
					)}
				</TitleWrapper>

				{/* Form Card */}
				<FormCard>
					<form className="flex flex-col gap-6 w-full">
						<div className={cn("flex relative", { "mb-3": formState.errors.name })}>
							<StyledInput
								type="text"
								data-error={!!formState.errors.name}
								placeholder={t("contactUs.placeholders.name")}
								{...register("name")}
							/>
							{formState.errors.name && (
								<p className="field-error absolute bottom-0 translate-y-full left-0 m-0 text-xs pt-1">
									{t(`contactUs.validation.${formState.errors.name.message}`)}
								</p>
							)}
						</div>

						<div className={cn("flex relative", { "mb-6": formState.errors.email })}>
							<StyledInput
								type="email"
								data-error={!!formState.errors.email}
								placeholder={t("contactUs.placeholders.email")}
								{...register("email")}
							/>
							{formState.errors.email && (
								<p className="field-error absolute bottom-0 translate-y-full left-0 m-0 text-xs pt-1">
									{formState.errors.email.message === "emailMustBeCorporate"
										? t("contactUs.validation.emailMustBeCorporate")
										: t(`contactUs.validation.${formState.errors.email.message}`)}
								</p>
							)}
						</div>

						<div className={cn("flex relative", { "mb-3": phoneTouched && formState.errors.phone })}>
							<StyledPhoneInputWrapper data-error={phoneTouched && !!formState.errors.phone}>
								<PhoneInput
									defaultCountry="br"
									value={phone}
									onChange={(phone) => setPhone(phone)}
									onBlur={() => {
										setPhoneTouched(true);
										trigger("phone");
									}}
								/>
							</StyledPhoneInputWrapper>
							{phoneTouched && formState.errors.phone && (
								<p className="field-error absolute bottom-0 translate-y-full left-0 m-0 text-xs pt-1">
									{t(`contactUs.validation.${formState.errors.phone.message}`)}
								</p>
							)}
						</div>

						<div className={cn("flex relative", { "mb-3": formState.errors.company })}>
							<StyledInput
								type="text"
								data-error={!!formState.errors.company}
								placeholder={t("contactUs.placeholders.company")}
								{...register("company")}
							/>
							{formState.errors.company && (
								<p className="field-error absolute bottom-0 translate-y-full left-0 m-0 text-xs pt-1">
									{t(`contactUs.validation.${formState.errors.company.message}`)}
								</p>
							)}
						</div>

						<div className={cn("flex relative", { "mb-3": formState.errors.subject })}>
							<StyledTextarea
								data-error={!!formState.errors.subject}
								placeholder={t("contactUs.placeholders.message")}
								{...register("subject")}
							/>
							{formState.errors.subject && (
								<p className="field-error absolute bottom-0 translate-y-full left-0 m-0 text-xs pt-1">
									{t(`contactUs.validation.${formState.errors.subject.message}`)}
								</p>
							)}
						</div>
					</form>

					<SubmitButton
						onClick={hookFormHandleSubmit(handleSubmit)}
						type="button"
						disabled={!formState.isValid || formState.isSubmitting}
					>
						{formState.isSubmitting ? (
							<AiOutlineLoading className="animate-spin" />
						) : (
							<>
								<span>{buttonText || t("contactUs.inputField.send")}</span>
								<HiArrowRight />
							</>
						)}
					</SubmitButton>

					<p className="text-xs text-[#A3A6A6] mt-3 mb-0">
						Ao enviar, você concorda com nossa{" "}
						<a
							href="/pt/privacy-policy"
							target="_blank"
							rel="noreferrer"
							className="text-white underline"
						>
							Política de Privacidade
						</a>
						.
					</p>
				</FormCard>

				{/* Locations */}
				<LocationsWrapper>
					<LocationsText
						dangerouslySetInnerHTML={{
							__html: !isMobile
								? t("contactUs.locations.description.desktop")
								: t("contactUs.locations.description.mobile"),
						}}
					/>
					<LocationsGrid>
						{locations.map((location) => (
							<Location
								key={location.location}
								itemScope
								itemType="https://schema.org/Organization"
							>
								<Flag src={location.flag} alt={location.location} />
								<LocationTitle itemProp="address">
									{location.location}
								</LocationTitle>
								<LocationPhone
									itemProp="telephone"
									content={location.contacts.phoneNumber}
								>
									{location.phone}
								</LocationPhone>
								<ContactIcons>
									<ContactIcon
										href={`tel:${location.contacts.phoneNumber}`}
										aria-label={`Ligar para ${location.location}`}
										title={`Ligar para ${location.location}`}
										itemProp="telephone"
									>
										<PhoneCallIcon size={24} />
									</ContactIcon>
									<ContactIcon
										href={`https://wa.me/${location.contacts.whatsappNumber}`}
										target="_blank"
										rel="noopener noreferrer"
										aria-label={`WhatsApp ${location.location}`}
										title={`WhatsApp ${location.location}`}
									>
										<FaWhatsapp />
									</ContactIcon>
									<ContactIcon
										href={`mailto:${location.contacts.email}`}
										aria-label={`Enviar e-mail para ${location.location}`}
										title={`Enviar e-mail para ${location.location}`}
										itemProp="email"
									>
										<EnvelopeSimpleIcon size={24} />
									</ContactIcon>
								</ContactIcons>
								<meta itemProp="name" content="Verzel" />
								<meta itemProp="url" content="https://verzel.com.br" />
							</Location>
						))}
					</LocationsGrid>
				</LocationsWrapper>
			</ContentWrapper>

			{feedback && (
				<FeedbackOverlay
					// clicar fora fecha; o clique dentro do card não propaga
					onClick={() => setFeedback(null)}
					role="dialog"
					aria-modal="false"
					aria-labelledby="contact-feedback-title"
				>
					<FeedbackCard
						$variant={feedback}
						onClick={(event) => event.stopPropagation()}
					>
						<FeedbackClose
							type="button"
							onClick={() => setFeedback(null)}
							aria-label={t("contactUs.feedback.close")}
						>
							<HiX />
						</FeedbackClose>

						<FeedbackIcon $variant={feedback}>
							{feedback === "success" ? <HiCheckCircle /> : <HiExclamationCircle />}
						</FeedbackIcon>

						<FeedbackTitle id="contact-feedback-title">
							{feedback === "success"
								? t("contactUs.feedback.successTitle")
								: t("contactUs.feedback.errorTitle")}
						</FeedbackTitle>

						<FeedbackText>
							{feedback === "success"
								? t("contactUs.feedback.successText")
								: t("contactUs.feedback.errorText")}
						</FeedbackText>
					</FeedbackCard>
				</FeedbackOverlay>
			)}
		</Section>
	);
}
